import { styled } from '@porsche-code/kado-styled-system/jsx';
import { PInlineNotification } from '@porsche-design-system/components-react';
import type { TElement } from 'platejs';
import {
  Plate,
  PlateContent,
  useEditorState,
  usePlateEditor,
  usePluginOption,
} from 'platejs/react';
import { useCallback, useEffect, useState } from 'react';

import { Label } from '../Label';
import { Message } from '../Message';
import { Spacer } from '../Spacer';
import { SpinnerOverlay } from '../Spinner';
import { isEmptyValue } from './isEmptyValue';
import {
  DropPlugin,
  imagePlugin,
  PastePlugin,
  PlaceholderTokenButtons,
} from './plugins';
import { RichText } from './RichText';
import { Toolbar } from './Toolbar';
import { type EditorToolbarPreset } from './toolbarPresets';
import { UploadAccessibilityModal } from './UploadAccessibilityModal';
import { usePlateId } from './usePlateId';
import { usePlugins } from './usePlugins';
import { useSingleLinePlugins } from './useSingleLinePlugins';

const Wrapper = styled('div', {
  base: {
    backgroundColor: 'background.frosted',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'border.contrastLower',
    borderRadius: 'xl',
    paddingBlock: '13px',
    paddingInline: 'md',
    position: 'relative',
    '&:focus-within': {
      borderColor: 'border.primary',
    },
    '&:hover': {
      borderColor: 'border.primary',
    },
  },

  variants: {
    disabled: {
      true: {
        cursor: 'not-allowed',
        color: 'text.primary',
        opacity: 0.4,
        '&:hover': {
          borderColor: 'border.contrastLower',
        },
      },
    },
  },
});

export const SingleLineEditor = <
  TMentionComponentValue extends { type: string },
  TMentionComponentPropsExtension,
>({
  id: propsId,
  testId,
  label,
  required,
  autoFocus,
  disabled,
  message,
  initialValue,
  onChange,
  MentionLabel,
  mentionProps,
  onPasteMention,
  keepFormattingsOnPaste,
  placeholderTokens,
  placeholderTokenTranslations,
  placeholderTokenInfo,
}: {
  id?: string;
  testId?: string;
  label?: string;
  required?: boolean;
  autoFocus?: boolean;
  disabled?: boolean;
  message?: string;
  initialValue?: TElement[];
  onChange?: (value: TElement[]) => void;
  MentionLabel?: React.FunctionComponent<
    { value: TMentionComponentValue } & TMentionComponentPropsExtension
  >;
  mentionProps?: TMentionComponentPropsExtension;
  onPasteMention?: (mention: TMentionComponentValue) => boolean;
  keepFormattingsOnPaste?: boolean;
  placeholderTokens?: readonly string[];
  placeholderTokenTranslations?: Record<string, string>;
  placeholderTokenInfo?: string;
}) => {
  const id = usePlateId(propsId);

  const { plugins, components } = useSingleLinePlugins({
    MentionLabel,
    mentionProps,
    onPasteMention,
    keepFormattingsOnPaste,
  });

  const editor = usePlateEditor({
    id,
    plugins,
    override: { components },
    value: initialValue,
    nodeId: false,
  });

  if (disabled) {
    return (
      <>
        {label && <Label label={label} isRequired={required} isDisabled />}

        <Wrapper {...(testId && { 'data-testid': testId })} disabled>
          <RichText value={initialValue ?? []} />
        </Wrapper>
      </>
    );
  }

  return (
    <Plate
      editor={editor}
      onChange={
        onChange ? ({ value }) => onChange(value as TElement[]) : undefined
      }
    >
      {label && <Label label={label} isRequired={required} />}

      {placeholderTokens && (
        <>
          {placeholderTokenInfo && (
            <PlaceholderTokenInfo>{placeholderTokenInfo}</PlaceholderTokenInfo>
          )}
          <PlaceholderTokenButtons
            tokens={placeholderTokens}
            labelResolver={(token) =>
              placeholderTokenTranslations
                ? placeholderTokenTranslations[token]
                : token
            }
          />
          <Spacer mb="sm" />
        </>
      )}

      <Wrapper {...(testId && { 'data-testid': testId })}>
        {required && (
          <RequiredInput
            isEmpty={!initialValue || isEmptyValue(initialValue)}
            autoFocus={autoFocus}
          />
        )}
        <PlateContent
          style={{ outline: 'none' }}
          aria-label={label}
          autoFocus={autoFocus}
          onFocus={() => {
            if (!editor.selection) {
              editor.tf.select(editor.api.end([]));
            }
          }}
        />
      </Wrapper>
      <Message value={message} />
    </Plate>
  );
};

const PlaceholderTokenInfo = styled('p', {
  base: {
    marginBottom: 'sm',
    color: 'text.contrastMedium',
    fontSize: 'sm',
  },
});

export const Editor = <
  TMentionComponentValue extends { type: string },
  TMentionComponentPropsExtension,
>({
  id: propsId,
  testId,
  label,
  hideLabel,
  required,
  disabled,
  message,
  size,
  initialValue,
  onChange,
  hasCharactersCounter,
  onUploadImage,
  onCancelImageUpload,
  allowedImageTypes,
  allowPublicImageUpload = 'disallowed',
  MentionLabel,
  mentionProps,
  onPasteMention,
  i18n,
  toolbarPreset = 'default',
}: {
  id?: string;
  testId?: string;
  label?: string;
  hideLabel?: boolean;
  required?: boolean;
  disabled?: boolean;
  message?: string;
  size?: 'small' | 'default';

  initialValue?: TElement[];
  onChange?: (value: TElement[]) => void;

  hasCharactersCounter?: boolean;

  onUploadImage?: (
    fileOrData: File | string,
    opt: {
      uploadAsPubliclyAccessible?: boolean;
      onProgress?: (value: number) => void;
    },
  ) => Promise<string>;
  onCancelImageUpload?: () => void;
  allowedImageTypes?: { type: string; extension: string }[];
  allowPublicImageUpload?: 'allowed' | 'required' | 'disallowed';

  MentionLabel?: React.FunctionComponent<
    { value: TMentionComponentValue } & TMentionComponentPropsExtension
  >;
  mentionProps?: TMentionComponentPropsExtension;
  onPasteMention?: (mention: TMentionComponentValue) => boolean;
  toolbarPreset?: EditorToolbarPreset;

  i18n: {
    gdprTooltip: string;
    boldLabel?: string;
    underlineLabel?: string;
    italicLabel?: string;
    strikethroughLabel?: string;
    alignLeftLabel?: string;
    alignCenterLabel?: string;
    alignRightLabel?: string;
    alignJustifyLabel?: string;
    outdentLabel?: string;
    indentLabel?: string;
    insertLinkLabel?: string;
    applyLinkLabel?: string;
    removeLinkLabel?: string;
    sanitizeLinkInstruction?: string;
    bulletedListLabel?: string;
    numberedListLabel?: string;
    insertTableLabel?: string;
    columnLabel?: string;
    rowLabel?: string;
    undoLabel?: string;
    redoLabel?: string;
    captionPlaceholder?: string;
    dropzoneDescription?: string;
    imageUploadHeading?: string;
    dragDescription?: string;
    allowedTypesDescription?: string;
    uploadButtonText?: string;
    resetButtonText?: string;
    uploadErrorHeading?: string;
    uploadErrorDescription?: string;
    publicImageUploadText?: string;
    cidImagesStrippedNotification?: string;
    fontSizeLabel?: string;
    textColorLabel?: string;
    backgroundColorLabel?: string;
    clearTextColorLabel?: string;
    clearBackgroundColorLabel?: string;
  };
}) => {
  const id = usePlateId(propsId);

  const [showCidStrippedNotification, setShowCidStrippedNotification] =
    useState(false);

  const onStripCidImages = useCallback(() => {
    setShowCidStrippedNotification(true);
  }, []);

  const { plugins, components } = usePlugins({
    MentionLabel,
    mentionProps,
    onPasteMention,
    onStripCidImages,
    allowedImageTypes,
    allowPublicImageUpload,
    toolbarPreset,
  });

  const editor = usePlateEditor({
    id,
    plugins,
    override: {
      components,
      plugins: {
        [imagePlugin.key]: {
          options: {
            onUploadImage,
            captionPlaceholder: i18n?.captionPlaceholder,
          },
        },
      },
    },
    value: initialValue,
    nodeId: false,
    options: {},
  });

  useEffect(() => {
    editor.setOption(
      DropPlugin,
      'allowPublicImageUpload',
      allowPublicImageUpload,
    );
    editor.setOption(
      PastePlugin,
      'allowPublicImageUpload',
      allowPublicImageUpload,
    );
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [allowPublicImageUpload]);

  if (disabled) {
    return (
      <>
        {label && !hideLabel ? (
          <Label label={label} isRequired={required} isDisabled />
        ) : null}

        <Wrapper {...(testId && { 'data-testid': testId })} disabled>
          <RichText value={initialValue ?? []} includeImages />
          &nbsp;
        </Wrapper>
      </>
    );
  }

  return (
    <Plate
      editor={editor}
      onChange={
        onChange
          ? ({ value }) => {
              const currentValue = value as TElement[];
              const currentValueIsEmpty = isEmptyValue(currentValue);
              const initialValueWasEffectivelyEmpty =
                initialValue === undefined || isEmptyValue(initialValue);

              // Condition 1: An initial value was given, and the editor now returns an empty value.
              const clearedExistingValue =
                !initialValueWasEffectivelyEmpty && currentValueIsEmpty;

              // Condition 2: No initial value (or it was empty), and the editor now returns a non-empty value.
              const populatedEmptyValue =
                initialValueWasEffectivelyEmpty && !currentValueIsEmpty;

              // Condition 3: An initial value was given, the editor returns a non-empty value,
              // AND this new value is different from the initial value.
              const changedExistingValue =
                !initialValueWasEffectivelyEmpty &&
                !currentValueIsEmpty &&
                JSON.stringify(initialValue) !== JSON.stringify(currentValue);

              if (
                clearedExistingValue ||
                populatedEmptyValue ||
                changedExistingValue
              ) {
                onChange(currentValue);
              }
            }
          : undefined
      }
    >
      {label && !hideLabel ? (
        <Label label={label} isRequired={required} />
      ) : null}

      <Toolbar
        hasCharactersCounter={hasCharactersCounter}
        gdprTooltip={i18n.gdprTooltip}
        toolbarPreset={toolbarPreset}
        imageUpload={
          onUploadImage
            ? {
                onUploadImage,
                onCancelImageUpload,
                allowedImageTypes,
                allowPublicImageUpload,
                i18n,
              }
            : undefined
        }
        i18n={i18n}
      />

      <Wrapper {...(testId && { 'data-testid': testId })}>
        {required && (
          <RequiredInput
            isEmpty={!initialValue || isEmptyValue(initialValue)}
          />
        )}
        <Content size={size} ariaLabel={label} />
      </Wrapper>

      <Message value={message} />

      {onUploadImage &&
        i18n.imageUploadHeading &&
        i18n.publicImageUploadText &&
        i18n.uploadButtonText &&
        i18n.resetButtonText &&
        ['allowed', 'required'].includes(allowPublicImageUpload) && (
          <UploadAccessibilityModal
            heading={i18n.imageUploadHeading}
            publicImageUploadText={i18n.publicImageUploadText}
            uploadButtonText={i18n.uploadButtonText}
            resetButtonText={i18n.resetButtonText}
          />
        )}

      {showCidStrippedNotification && i18n.cidImagesStrippedNotification && (
        <>
          <Spacer mb="sm" />
          <PInlineNotification
            state="warning"
            description={i18n.cidImagesStrippedNotification}
            onDismiss={() => setShowCidStrippedNotification(false)}
          />
        </>
      )}
    </Plate>
  );
};

const RequiredInput = ({
  isEmpty,
  autoFocus,
}: {
  isEmpty: boolean;
  autoFocus?: boolean;
}) => {
  const editor = useEditorState();

  useEffect(() => {
    if (autoFocus) {
      editor.tf.select(editor.api.end([]));
      editor.tf.focus();
    }
  }, [autoFocus, editor]);

  return (
    <RequiredInputFix
      tabIndex={-1}
      autoComplete="off"
      value={isEmpty ? '' : 'not-empty'}
      onChange={() => {
        // noop
      }}
      onFocus={(evt: React.FocusEvent<HTMLInputElement>) => {
        evt.preventDefault();
        editor.tf.focus();
      }}
      required
    />
  );
};

const RequiredInputFix = styled('input', {
  base: {
    opacity: 0,
    height: '100%',
    width: '100%',
    bottom: 0,
    position: 'absolute',
  },
});

const Content = ({
  size,
  ariaLabel,
}: {
  size?: 'small' | 'default';
  ariaLabel?: string;
}) => {
  const isLocked = usePluginOption(PastePlugin, 'isUploadingInlineImages');

  return (
    <>
      <PlateContent
        style={{
          outline: 'none',
          minHeight: size === 'small' ? '80px' : '200px',
        }}
        aria-label={ariaLabel}
        // Disables editing while uploads are in progress.
        readOnly={isLocked}
      />
      {isLocked ? <SpinnerOverlay /> : null}
    </>
  );
};

export type { TElement };
